import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { notification } from "../../utils/Notifications.tsx";
import { createPermission } from "../../services/permissions.service.ts";
import PermissionFormEditFilesCreate from "../../components/permissions/PermissionFormEditFilesCreate.tsx";
import PermissionFormEditCreate from "../../components/permissions/PermissionFormEditCreate.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";

const NewPermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const isCreate = true;
  const isPartner = false;

  const validationSchema = Yup.object().shape({
    description: Yup.string().required("La descripción es obligatoria"),
    startRequested: Yup.number().required("La fecha de inicio es obligatoria"),
    endRequested: Yup.number().required("La fecha de fin es obligatoria"),
    daysRequested: Yup.number().required("Campo requerido").min(1).max(7),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      description: "",
      startRequested: "",
      endRequested: "",
      daysRequested: 1,
      dateRange: [],
      permissionFiles: [],
      hasExceededMaxDays: false,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const permissionData = {
          description: values.description,
          startRequested: values.startRequested,
          endRequested: values.endRequested,
          daysRequested: values.daysRequested,
          isRequest: true,
          approved: false,
          isRejected: false,
        };

        await createPermission(Number(id), permissionData, values.permissionFiles);
        notification("Éxito", "Permiso creado correctamente", "success");
        navigate(`/colaboradores/detalles/${id}`);
      } catch (error) {
        console.error("Error al crear el permiso:", error);
        notification("Error", "No se pudo crear el permiso", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Layout>
      <Title
        baseTitle="Crear permiso"
        basePath={`/colaboradores/nuevo-permiso/${id}`}
        title="Información del colaborador"
      />

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Datos generales del permiso
          </PanelHeader>
          <PermissionFormEditCreate
            formik={formik}
            isPartner={isPartner}
            isCreate={isCreate}
          />
        </Panel>
      </div>

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            Archivos adjuntos del permiso
          </PanelHeader>
          <PermissionFormEditFilesCreate formik={formik} reloadFiles={null} />
        </Panel>
      </div>

      {/* Badge de validación de días */}
      {formik.values.startRequested && formik.values.endRequested && (
        <div className="card border-0 m-4 rounded">
          <div className="card-body">
            {(() => {
              const startDate = dayjs.unix(formik.values.startRequested);
              const endDate = dayjs.unix(formik.values.endRequested);
              const daysDiff = endDate.diff(startDate, 'day') + 1;
              
              if (daysDiff > 7) {
                return (
                  <div className="alert alert-danger d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-3 fs-4"></i>
                    <div>
                      <strong>Límite excedido:</strong> El permiso no puede exceder 7 días. 
                      Has seleccionado {daysDiff} días. Por favor ajusta las fechas.
                    </div>
                  </div>
                );
              }
              
              if (startDate.isBefore(dayjs().add(1, 'day'))) {
                return (
                  <div className="alert alert-warning d-flex align-items-center">
                    <i className="fas fa-info-circle me-3 fs-4"></i>
                    <div>
                      <strong>Antelación requerida:</strong> El permiso debe solicitarse con al menos 1 día de antelación.
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="alert alert-success d-flex align-items-center">
                  <i className="fas fa-check-circle me-3 fs-4"></i>
                  <div>
                    <strong>Rango válido:</strong> {daysDiff} día{daysDiff > 1 ? 's' : ''} solicitado{daysDiff > 1 ? 's' : ''} 
                    del {startDate.format('DD/MM/YYYY')} al {endDate.format('DD/MM/YYYY')}.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          <div className="col-md-3">
            <Button
              onClick={() => formik.handleSubmit()}
              title={formik.values.hasExceededMaxDays ? "Ajusta las fechas primero" : "Guardar datos del permiso"}
              variant="azul"
              disabled={loading || formik.values.hasExceededMaxDays}
              type="submit"
            />
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default NewPermission; 